import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PaymentIcon from '@mui/icons-material/Payment';
import dayjs from 'dayjs';

const B = { primary: '#4357AD', dark: '#1E2A4A' };
const cardSx = { borderRadius: 4, boxShadow: '0 2px 12px rgba(67,87,173,0.08)', bgcolor: '#fff' };

const lessonTypeLabels = {
  REGULAR: 'Звичайний',
  INDIVIDUAL: 'Індивідуальний',
  ADDITIONAL: 'Додатковий',
};

const methodLabels = {
  CASH: 'Готівка',
  BANK_TRANSFER: 'Переказ',
};

const PaymentHistory = ({ payments, isLoading, onAddClick, onEdit, onDelete, canManage = false }) => (
  <Card sx={cardSx}>
    <CardContent sx={{ py: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#EDE9FE', color: B.primary }}>
          <PaymentIcon />
        </Box>
        <Typography variant="h6" fontWeight={700} sx={{ color: B.dark }}>
          Оплати
        </Typography>
        {(payments?.length > 0) && (
          <Chip label={payments.length} size="small" sx={{ bgcolor: '#EDE9FE', color: B.primary, fontWeight: 700, fontSize: 12 }} />
        )}
        {canManage && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={onAddClick}
            sx={{ ml: 'auto !important', textTransform: 'none', color: B.primary, fontWeight: 600, borderRadius: 3 }}
          >
            Додати оплату
          </Button>
        )}
      </Stack>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress sx={{ color: B.primary }} />
        </Box>
      ) : (!payments || payments.length === 0) ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: '#8891A5' }}>Записів про оплату поки немає.</Typography>
        </Box>
      ) : (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#F8F7FF', '& th': { color: B.primary, fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.8, borderBottom: '2px solid #EDE9FE', py: 1.5 } }}>
                <TableCell>Дата</TableCell>
                <TableCell>Сума</TableCell>
                <TableCell>Тип уроку</TableCell>
                <TableCell>Спосіб</TableCell>
                <TableCell align="center">Занять</TableCell>
                <TableCell>Примітки</TableCell>
                {canManage && <TableCell align="right">Дії</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {payments.map((payment, idx) => (
                <TableRow key={payment.id} sx={{ bgcolor: idx % 2 === 0 ? '#fff' : '#FAFAFF', '&:hover': { bgcolor: '#EDE9FE' }, '& td': { py: 1.5, borderBottom: '1px solid #F1F0FB' } }}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} sx={{ color: B.dark }}>
                      {dayjs(payment.paymentDate).format('DD.MM.YYYY')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700} sx={{ color: '#22C55E' }}>
                      {payment.amount} ₴
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={lessonTypeLabels[payment.lessonType] || payment.lessonType}
                      size="small"
                      sx={{ bgcolor: '#F3F0FF', color: B.primary, fontWeight: 600, fontSize: 12, height: 24 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#475569' }}>
                      {methodLabels[payment.paymentMethod] || payment.paymentMethod}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={payment.lessonsCovered}
                      size="small"
                      sx={{ bgcolor: '#D1FAE5', color: '#065F46', fontWeight: 700, fontSize: 12, height: 24, minWidth: 32 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ color: '#8891A5', maxWidth: { xs: 120, md: 180 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {payment.notes || '—'}
                    </Typography>
                  </TableCell>
                  {canManage && (
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Редагувати">
                          <IconButton size="small" onClick={() => onEdit?.(payment)} sx={{ color: B.primary, '&:hover': { bgcolor: '#EDE9FE' } }}>
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Видалити">
                          <IconButton size="small" onClick={() => onDelete?.(payment)} sx={{ color: '#EF4444', '&:hover': { bgcolor: '#FEE2E2' } }}>
                            <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </CardContent>
  </Card>
);

export default PaymentHistory;
